import React from 'react';

const PageTableLayout = ({ 
  title, 
  breadcrumb, 
  columns, 
  data, 
  filters, 
  actions = [],
  loading = false,
  pagination = null,
  onPageChange,
  showFiltersInTable = false
}) => {
  return (
    <div className="p-6">
    {/* Breadcrumb */}
    <nav className="text-xs text-gray-500 mb-2" aria-label="Breadcrumb">
      {breadcrumb}
    </nav>

    {/* Header */}
    {actions?.length>0&&<div className="flex items-center justify-between mb-4">
      <h2 className="text-2xl font-bold">{title}</h2>
      <div className="flex space-x-2">
        {actions.map((action, index) => (
          <button
            key={index}
            className={`${action.className || 'bg-gray-600 hover:bg-gray-700'} text-white px-4 py-2 rounded text-sm`}
            onClick={action.onClick}
          >
            {action.label}
          </button>
        ))}
      </div>
    </div>}

    {/* Filters - only show if not showing in table and filters exist */}
    {!showFiltersInTable && filters && (
      <div className="flex flex-wrap gap-2 mb-4">{filters}</div>
    )}

    {/* Table */}
    <div className="bg-white shadow rounded overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-100">
          <tr>
            {columns.map((col) => (
              <th key={col.key} className="px-4 py-2 text-left font-semibold text-gray-700">
                {col.title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {showFiltersInTable && (
            <tr className="bg-gray-50 border-b">
              {columns.map((col) => (
                <td key={`filter-${col.key}`} className="px-4 py-2">
                  {col.filterComponent}
                </td>
              ))}
            </tr>
          )}
          
          {loading ? (
            <tr>
              <td colSpan={columns.length} className="px-4 py-6 text-center text-gray-400">
                <div className="flex justify-center items-center">
                  <svg className="animate-spin h-5 w-5 mr-3 text-blue-600" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Loading...
                </div>
              </td>
            </tr>
          ) : data.length === 0 ? (
            <tr>
              <td colSpan={columns.length + 0} className="px-4 py-6 text-center text-gray-400">No records found.</td>
            </tr>
          ) : (
            data.map((row, idx) => (
              <tr key={row.id || idx} className="border-b hover:bg-gray-50">
                {columns.map((col) => (
                  <td key={col.key} className="px-4 py-2 whitespace-nowrap">
                    {col.render ? col.render(row) : row[col.key]}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
    
    {/* Pagination */}
    {pagination && (
      <div className="flex items-center justify-between mt-4 text-sm">
        <div className="text-gray-600">
          Total Records: {pagination.totalRecords || 0}
        </div>
        <div className="flex items-center space-x-4">
          {pagination.onLimitChange && (
            <div className="flex items-center">
              <span className="text-gray-600 mr-2">Show:</span>
              <select
                className="border rounded px-2 py-1 text-sm"
                value={pagination.limit || 10}
                onChange={(e) => pagination.onLimitChange(Number(e.target.value))}
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>
          )}
          <div className="flex space-x-1">
          <button 
            onClick={() => onPageChange(1)} 
            disabled={pagination.currentPage <= 1}
            className={`px-3 py-1 rounded ${pagination.currentPage <= 1 ? 'bg-gray-200 text-gray-500' : 'bg-gray-200 hover:bg-gray-300'}`}
          >
            «
          </button>
          <button 
            onClick={() => onPageChange(pagination.currentPage - 1)} 
            disabled={pagination.currentPage <= 1}
            className={`px-3 py-1 rounded ${pagination.currentPage <= 1 ? 'bg-gray-200 text-gray-500' : 'bg-gray-200 hover:bg-gray-300'}`}
          >
            ‹
          </button>
          
          {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
            // Calculate page numbers to show, centered around current page
            let pageNum;
            if (pagination.totalPages <= 5) {
              pageNum = i + 1;
            } else if (pagination.currentPage <= 3) {
              pageNum = i + 1;
            } else if (pagination.currentPage >= pagination.totalPages - 2) {
              pageNum = pagination.totalPages - 4 + i;
            } else {
              pageNum = pagination.currentPage - 2 + i;
            }
            
            return (
              <button 
                key={pageNum} 
                onClick={() => onPageChange(pageNum)}
                className={`px-3 py-1 rounded ${pagination.currentPage === pageNum ? 'bg-blue-600 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}
              >
                {pageNum}
              </button>
            );
          })}
          
          <button 
            onClick={() => onPageChange(pagination.currentPage + 1)} 
            disabled={pagination.currentPage >= pagination.totalPages}
            className={`px-3 py-1 rounded ${pagination.currentPage >= pagination.totalPages ? 'bg-gray-200 text-gray-500' : 'bg-gray-200 hover:bg-gray-300'}`}
          >
            ›
          </button>
          <button 
            onClick={() => onPageChange(pagination.totalPages)} 
            disabled={pagination.currentPage >= pagination.totalPages}
            className={`px-3 py-1 rounded ${pagination.currentPage >= pagination.totalPages ? 'bg-gray-200 text-gray-500' : 'bg-gray-200 hover:bg-gray-300'}`}
          >
            »
          </button>
          </div>
        </div>
      </div>
    )}
    </div>
  );
};

export default PageTableLayout;
